import {
  PageLoader,
  Banner,
  Box,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  Flex,
  Grid,
  Icon,
  PlainButton,
} from '@customerio/pluma-components/react';
import { PageTitle } from '@/components/page-title';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { useServicesQuery } from '@/services/api';
import { useNavigateToAgentChat } from '@/features/copilot';
import { useAnalytics } from '@/services/analytics';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { AgentLayout } from '../layout';
import { RoutineDetail } from './components/routine-detail';
import { useState } from 'react';
import clsx from 'clsx';
import * as styles from './index.css';

const CREATE_ROUTINE_MESSAGE =
  "I'd like to create a new routine. Ask me what I want to keep track of, how often I'd like updates, and what I should know in each summary.";

interface RoutineSuggestion {
  title: string;
  description: string;
  message: string;
}

const ROUTINE_SUGGESTIONS: RoutineSuggestion[] = [
  {
    title: 'Deliverability watchdog',
    description: 'Monitor bounce rates, spam complaints, and domain reputation trends.',
    message:
      "Monitor my email deliverability health on an ongoing basis. Track bounce rates, spam complaint rates, suppression list growth, and domain reputation signals. If anything is trending in the wrong direction — even gradually — flag it immediately and explain what's likely causing it, how severe it is, and what concrete steps I should take to fix it before it impacts my sending reputation.",
  },
  {
    title: 'Broadcast recap',
    description: 'Compare how recent broadcasts performed and surface actionable takeaways.',
    message:
      'Summarize how my recent broadcasts performed. For each one, pull open rates, click rates, unsubscribe rates, and any revenue attributed. Compare them against each other so I can see which subject lines, send times, or content approaches are actually working. Call out the top performers and the underperformers, and give me specific, actionable takeaways I can apply to my next broadcast.',
  },
  {
    title: 'Segment hygiene check',
    description: 'Audit segments for unused, empty, or redundant conditions.',
    message:
      "Audit all of my segments and flag anything that needs cleanup. I want to know about unused segments that aren't referenced by any active campaign or workflow, segments with zero or near-zero members, segments with overlapping or redundant conditions, and segments that haven't been touched or referenced in over 90 days. For each issue you find, recommend whether I should update, merge, or delete it.",
  },
  {
    title: 'Weekly goal conversion report',
    description: 'Track conversion rates and week-over-week trends across all goals.',
    message:
      'Every Monday morning, pull conversion data across all of my goals and send me a summary. For each goal, include the conversion rate, week-over-week trend, and which campaigns or workflows are driving the most attributed conversions and revenue. Highlight anything that improved or declined significantly so I know where to focus my attention for the week ahead.',
  },
  {
    title: 'Subscriber churn early warning',
    description: 'Spot at-risk subscribers before they disengage or unsubscribe.',
    message:
      "Scan my active subscriber base for early warning signs of churn. Look for patterns like declining email engagement over the last 30–60 days, inactivity streaks, or customers who match the behavioral profile of people who've previously unsubscribed or gone dormant. Flag the at-risk subscribers and recommend which existing workflows or campaigns I should use to re-engage them — or suggest a retention campaign if one doesn't exist yet.",
  },
  {
    title: 'Audience growth tracker',
    description: 'Track how key segments are growing or shrinking over time.',
    message:
      'Track the size of my key segments over time by logging their membership counts on a daily basis. Over weeks, build a trend line for each one so I can see whether my high-value segments are growing, whether my "lapsed" or "inactive" segments are ballooning, and where the overall momentum of my audience is heading. Surface any notable shifts — positive or negative — so I can connect them back to recent campaigns or product changes.',
  },
];

/**
 * Picks `count` random items from `ROUTINE_SUGGESTIONS` using a stable
 * selection per component mount via useState's lazy initializer.
 */
function useRandomSuggestions(count: number): RoutineSuggestion[] {
  const [suggestions] = useState(() => {
    const shuffled = [...ROUTINE_SUGGESTIONS];
    for (let index = shuffled.length - 1; index > 0; index--) {
      const index_ = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[index_]] = [shuffled[index_], shuffled[index]];
    }
    return shuffled.slice(0, count);
  });
  return suggestions;
}

export function RoutinesPage() {
  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const navigateToChat = useNavigateToAgentChat();
  const { track } = useAnalytics();
  const suggestions = useRandomSuggestions(2);

  const routinesQuery = useServicesQuery(
    'listCopilotCrons',
    { params: { environment_id: workspaceId } },
    { enabled: workspaceId > 0 },
  );

  const routines = routinesQuery.data?.routines ?? [];
  const routine = routines[0] ?? null;

  if (routinesQuery.isLoading) {
    return (
      <AgentLayout title="Routine">
        <PageTitle title="Routine" />
        <PageLoader label="Loading routine" />
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Routine">
      <PageTitle title="Routine" />
      <Box
        display="flex"
        flexDirection="column"
        flexGrow="1"
        minH="0"
        alignItems="center"
        justifyContent={routine == null ? 'center' : 'flex-start'}
        p="300"
      >
        {routinesQuery.isError ? (
          <Banner variant="error" title="Error">
            Unable to load routine. Please try again later.
          </Banner>
        ) : null}

        {routine == null && !routinesQuery.isError ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap="300">
            <EmptyState className={styles.fullWidthEmptyState}>
              <EmptyStateTitle icon="trigger-date">Set up a routine</EmptyStateTitle>
              <EmptyStateDescription>
                Your routine runs on a schedule and sends you a summary by email.
              </EmptyStateDescription>
              <EmptyStateActions w="full">
                <Grid columns={3} gap="200" w="full">
                  {suggestions.map((suggestion) => (
                    <GradientRing key={suggestion.title} radius={4} display="flex" h="full">
                      <PlainButton
                        className={clsx(styles.suggestionCard, gradientHoverTarget)}
                        onClick={() => {
                          track('aiAssistant', 'routineSuggestionClicked', { Title: suggestion.title });
                          navigateToChat({ message: suggestion.message });
                        }}
                      >
                        <Flex alignItems="center" gap="050">
                          <Icon name="sparkle" size="xs" color="subtle" />
                          <div className={styles.suggestionCardTitle}>{suggestion.title}</div>
                        </Flex>
                        <div className={styles.suggestionCardDescription}>{suggestion.description}</div>
                      </PlainButton>
                    </GradientRing>
                  ))}
                  <GradientRing radius={4} display="flex" h="full">
                    <PlainButton
                      className={clsx(styles.suggestionCard, gradientHoverTarget)}
                      onClick={() => {
                        track('aiAssistant', 'routineSuggestionClicked', { Title: 'Create a custom routine' });
                        navigateToChat({ message: CREATE_ROUTINE_MESSAGE });
                      }}
                    >
                      <Flex alignItems="center" gap="050">
                        <Icon name="add" size="xs" color="subtle" />
                        <div className={styles.suggestionCardTitle}>Create a custom routine</div>
                      </Flex>
                      <div className={styles.suggestionCardDescription}>
                        Describe what you want to track and how often you'd like updates.
                      </div>
                    </PlainButton>
                  </GradientRing>
                </Grid>
              </EmptyStateActions>
            </EmptyState>
          </Box>
        ) : null}

        {routine != null ? <RoutineDetail routine={routine} workspaceId={workspaceId} /> : null}
      </Box>
    </AgentLayout>
  );
}
